// The snap-or-slide state of cs-card-list. The list moves cards with a transform transition, so a
// changed position normally slides. Some passes must snap instead: while the first layout
// settles, after a resize, and for a card inserted after the first layout (it would otherwise fly
// in from the list origin). This class owns every flag behind that decision; the component asks
// it one question per card per pass and does all the DOM writes itself.
// The insert/FLIP ordering this depends on: design-docs/typescript-patterns.md,
// "cs-card-list: animating cards making space on insert".

export class CardListAnimationState {
  // true until the first layout has stopped changing while the list is visible
  initializing = true;
  hasBeenVisible = false;
  private layoutStable = false;
  private prevHash = '';
  // true while layout is actively changing, suppresses card position animations
  private layoutSettling = true;
  private layoutSettleTimer: ReturnType<typeof setTimeout> | null = null;
  // cards added after initial layout. Their first positioning pass snaps them
  // into place (no fly-in from the origin) while the existing cards still
  // animate to their new positions to make space. Cleared once each card's
  // position is set. A WeakSet so removed-before-positioning elements can't leak.
  private newlyAddedElements = new WeakSet<HTMLElement>();
  // every card already positioned at least once. A card in the list that is
  // not yet in this set is newly added.
  private positionedElements = new WeakSet<HTMLElement>();

  // settleMs: how long a settle lasts after its last trigger
  constructor(private settleMs: () => number) { }

  get isLayoutStable(): boolean {
    return this.layoutStable;
  }

  // mark layout as settling and schedule settle completion.
  // the timer resets on each call, so animations stay suppressed while layout keeps changing
  markSettling(): void {
    this.layoutSettling = true;
    if (this.layoutSettleTimer) {
      clearTimeout(this.layoutSettleTimer);
    }
    this.layoutSettleTimer = setTimeout(() => {
      this.layoutSettling = false;
    }, this.settleMs());
  }

  // while initializing, the layout counts as stable once two consecutive content checks see the same hash
  noteContentHash(hash: string): void {
    if (this.initializing) {
      this.layoutStable = (hash === this.prevHash);
      this.prevHash = hash;
    }
  }

  // the first layout is stable and visible: from now on cards animate, after one settle
  finishInitializing(): void {
    this.initializing = false;
    this.hasBeenVisible = true;
    this.markSettling();
  }

  // Call once per positioning pass, before any card is moved, with the cards about to be placed.
  // Detects cards inserted after the initial layout (see the design doc section above): they are
  // marked to snap, any active settle is ended, and the existing cards are returned so the caller
  // can re-enable their transitions and let them slide old→new (FLIP).
  beginPass(elems: HTMLElement[], dragAnimationsAllowed: boolean): HTMLElement[] {
    const transitionsToReenable: HTMLElement[] = [];
    if (!this.initializing && !dragAnimationsAllowed) {
      const freshlyInserted = elems.filter(e => !this.positionedElements.has(e));
      if (freshlyInserted.length > 0) {
        for (const e of freshlyInserted) {
          this.newlyAddedElements.add(e);
        }
        // `layoutSettling` is a single flag shared by all settle sources (init,
        // resize, visibility). Clearing it here is unconditional, so an insert
        // that happens to overlap an unrelated in-progress settle ends that
        // settle a little early — those cards then animate instead of snap.
        // That is benign (a slide rather than a snap) and the common case is an
        // insert with no concurrent settle, so we accept it rather than track
        // per-source settle state.
        this.layoutSettling = false;
        if (this.layoutSettleTimer) {
          clearTimeout(this.layoutSettleTimer);
          this.layoutSettleTimer = null;
        }
        for (const e of elems) {
          if (!this.newlyAddedElements.has(e)) {
            transitionsToReenable.push(e);
          }
        }
      }
    }
    // remember every card about to be positioned, so a later insert can tell which cards are new
    for (const e of elems) {
      this.positionedElements.add(e);
    }
    return transitionsToReenable;
  }

  // should this card jump to its new position instead of sliding there?
  // Drag operations opt out via dragAnimationsAllowed.
  shouldSnap(elem: HTMLElement, dragAnimationsAllowed: boolean): boolean {
    return (this.layoutSettling || this.newlyAddedElements.has(elem)) && !dragAnimationsAllowed;
  }

  // a snapped card has been painted in place; from now on it slides like any other card
  snapped(elem: HTMLElement): void {
    this.newlyAddedElements.delete(elem);
  }

  dispose(): void {
    if (this.layoutSettleTimer) {
      clearTimeout(this.layoutSettleTimer);
    }
  }
}
